import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Alert, App, Button, Modal, Space, Table, Tag, Typography } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import apiClient from '../../api/client';

const { Text } = Typography;

export interface BulkItem {
  workerId: string;
  name: string;
  url: string;
  state: 'queued' | 'running' | 'succeeded' | 'failed' | 'skipped';
  reason?: string;
  jobId?: string;
  software?: string;
}

export interface BulkRun {
  id: string;
  started: string;
  ended?: string;
  concurrency: number;
  items: BulkItem[];
  version?: string;
}

interface Preview {
  items: BulkItem[];
  version?: string;
  concurrency: number;
  artifactAvailable: boolean;
  run?: BulkRun | null;
}

const STATE_TAG: Record<BulkItem['state'], { color: string; label: string }> = {
  queued: { color: 'default', label: 'queued' },
  running: { color: 'processing', label: 'installing' },
  succeeded: { color: 'success', label: 'updated' },
  failed: { color: 'error', label: 'failed' },
  skipped: { color: 'warning', label: 'skipped' },
};

const columns: ColumnsType<BulkItem> = [
  {
    title: 'Worker',
    key: 'name',
    ellipsis: true,
    render: (_, r) => (
      <div style={{ minWidth: 0 }}>
        <Text style={{ display: 'block' }} ellipsis>{r.name}</Text>
        <Text type="secondary" style={{ fontSize: 12 }} ellipsis>{r.url}</Text>
      </div>
    ),
  },
  {
    title: 'State',
    key: 'state',
    width: 104,
    render: (_, r) => <Tag color={STATE_TAG[r.state].color} style={{ marginInlineEnd: 0 }}>{STATE_TAG[r.state].label}</Tag>,
  },
  {
    title: 'Why',
    key: 'reason',
    ellipsis: { showTitle: true },
    render: (_, r) => <Text type="secondary" style={{ fontSize: 12 }}>{r.reason || ''}</Text>,
  },
];

interface Props {
  open: boolean;
  onClose: () => void;
  /** Called when a run finishes, so the table can refresh. */
  onFinished: () => void;
}

/**
 * Update every worker that runs an older checker version, a few at a time.
 * Opens on a preview (who would be updated, who is skipped and why) behind a
 * warning, then follows the run. Closing the dialog does not stop the run —
 * it continues on the server, and reopening shows its progress.
 */
const UpdateAllModal: React.FC<Props> = ({ open, onClose, onFinished }) => {
  const { message } = App.useApp();
  const [preview, setPreview] = useState<Preview | null>(null);
  const [run, setRun] = useState<BulkRun | null>(null);
  const [starting, setStarting] = useState(false);
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const endedRef = useRef<string | undefined>(undefined);

  const stopPolling = () => {
    if (pollRef.current) clearInterval(pollRef.current);
    pollRef.current = null;
  };

  const poll = useCallback(async () => {
    try {
      const { data } = await apiClient.get('/admin/workers/update-all/status');
      const r: BulkRun | null = data.data;
      setRun(r);
      if (r?.ended) {
        stopPolling();
        if (endedRef.current !== r.id) {
          endedRef.current = r.id;
          onFinished();
        }
      }
    } catch {
      // keep the last known state; the next tick retries
    }
  }, [onFinished]);

  const startPolling = useCallback(() => {
    stopPolling();
    pollRef.current = setInterval(poll, 2000);
  }, [poll]);

  useEffect(() => {
    if (!open) {
      stopPolling();
      return;
    }
    setPreview(null);
    apiClient.get('/admin/workers/update-all')
      .then(({ data }) => {
        const p: Preview = data.data;
        setPreview(p);
        // A run that is still going is what the operator wants to see.
        if (p.run && !p.run.ended) {
          setRun(p.run);
          startPolling();
        } else {
          setRun(null);
        }
      })
      .catch(() => message.error('Could not load the worker list'));
    return stopPolling;
  }, [open, message, startPolling]);

  const start = async () => {
    setStarting(true);
    try {
      const { data } = await apiClient.post('/admin/workers/update-all');
      setRun(data.data);
      startPolling();
    } catch (err: any) {
      const busy = err.response?.status === 409 ? err.response?.data?.data?.run : null;
      if (busy) {
        setRun(busy);
        startPolling();
      } else {
        message.error(err.response?.data?.error || 'Could not start the update');
      }
    } finally {
      setStarting(false);
    }
  };

  const queued = preview?.items.filter((i) => i.state === 'queued') || [];
  const skipped = preview?.items.filter((i) => i.state === 'skipped') || [];
  const inRun = !!run;
  const done = run?.items.filter((i) => i.state === 'succeeded' || i.state === 'failed').length || 0;
  const total = run?.items.filter((i) => i.state !== 'skipped').length || 0;
  const failed = run?.items.filter((i) => i.state === 'failed').length || 0;

  const footer = inRun
    ? [<Button key="close" onClick={onClose}>{run?.ended ? 'Close' : 'Close — keeps running'}</Button>]
    : [
      <Button key="cancel" onClick={onClose}>Cancel</Button>,
      <Button
        key="go"
        type="primary"
        danger
        loading={starting}
        disabled={!preview || !preview.artifactAvailable || queued.length === 0}
        onClick={start}
      >
        Update {queued.length} worker{queued.length === 1 ? '' : 's'}
      </Button>,
    ];

  return (
    <Modal
      title="Update all workers"
      open={open}
      onCancel={onClose}
      footer={footer}
      width={720}
      destroyOnHidden
    >
      {!inRun && preview && (
        <Space direction="vertical" size={10} style={{ width: '100%' }}>
          {!preview.artifactAvailable && (
            <Alert type="error" showIcon message="This gateway carries no checker binary to install." />
          )}
          {preview.artifactAvailable && queued.length > 0 && (
            <Alert
              type="warning"
              showIcon
              message={`This starts installing version ${preview.version || 'current'} on ${queued.length} worker${queued.length === 1 ? '' : 's'} at once`}
              description={(
                <ul style={{ margin: 0, paddingLeft: 16, fontSize: 13 }}>
                  <li>{preview.concurrency} at a time; the rest wait their turn.</li>
                  <li>Each worker&apos;s checker restarts — a short gap in the checks it runs — and gets a new token.</li>
                  <li>Uses each host&apos;s saved SSH credential and confirmed host key. A host whose key changed stops, it is never sent the password.</li>
                </ul>
              )}
            />
          )}
          {preview.artifactAvailable && queued.length === 0 && (
            <Alert type="success" showIcon message="No worker can be updated right now." description={skipped.length ? 'See below why the rest are skipped.' : 'Every worker already runs this version.'} />
          )}
          {preview.items.length > 0 && (
            <Table
              columns={columns}
              dataSource={preview.items}
              rowKey="workerId"
              size="small"
              tableLayout="fixed"
              pagination={false}
              scroll={preview.items.length > 8 ? { y: 320 } : undefined}
            />
          )}
        </Space>
      )}

      {inRun && run && (
        <Space direction="vertical" size={10} style={{ width: '100%' }}>
          <Alert
            type={run.ended ? (failed ? 'warning' : 'success') : 'info'}
            showIcon
            message={run.ended
              ? `Finished: ${done - failed} updated${failed ? `, ${failed} failed` : ''}`
              : `Updating to ${run.version || 'current'}: ${done} of ${total} done, ${run.concurrency} at a time`}
            description={failed ? 'A failed worker can be retried with its own Update button, which shows the full install log.' : undefined}
          />
          <Table
            columns={columns}
            dataSource={run.items}
            rowKey="workerId"
            size="small"
            tableLayout="fixed"
            pagination={false}
            scroll={run.items.length > 8 ? { y: 360 } : undefined}
          />
        </Space>
      )}
    </Modal>
  );
};

export default UpdateAllModal;
